import dayjs from 'dayjs';
import React, { useEffect, useState, useRef } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BankActions, InvoiceActions } from '../../../../actions';
import Button from '../../../../components/ui/Button/Button';
import ContainerCard from '../../../../components/ui/ContainerCard';
import Error from '../../../../components/ui/Error/Error';
import DateInput from '../../../../components/ui/Inputs/DateInput/DateInput';
import SelectInput from '../../../../components/ui/Inputs/SelectInput/SelectInput';
import TextAreaInput from '../../../../components/ui/Inputs/TextAreaInput';
import TextInput from '../../../../components/ui/Inputs/TextInput';
import { notify } from '../../../../components/ui/Toast/Toast';
import { frenchFormat, standardFormat } from '../../../../utils';
import { getTypeId, getStatusId } from '../../../invoicesPage/functions';
import { fetchSelectData, generateRandomNumber, getErrors } from '../../../../utils';
import { RootState } from '../../../../reducers/Store';
import { z } from 'zod';
import { Bank, BankSchema } from '../../../../models/bank/bank';
import { InvoiceStatus, InvoiceType } from '../../../../types/types';
import { GetDealByIdSchemaType } from '../../../../services/dealService/_schema/getDealById.schema';

const NewInvoiceFormSchema = z.object({
    status: z.object({
        name: z.nativeEnum(InvoiceStatus)
    }).nullable(),
    billing_date: z.date().min(new Date(), {message: 'Veuillez sélectionner une date.'}),
    billable: z.number().min(0, {message: 'Le montant d\'honoraire doit être positif.'}).nullable(),
    expenses: z.number().min(0, {message: 'Le montant de frais doit être positif.'}).nullable(),
    bank: BankSchema.nullable(),
    type: z.object({
        name: z.nativeEnum(InvoiceType)
    }).nullable(),
    due_date: z.date().min(new Date(), {message: 'Veuillez sélectionner une date.'}),
    billable_vat_rate: z.number().min(1, {message: 'Veuillez saisir la tva d\'honoraires'}).nullable(),
    expenses_vat_rate: z.number().min(1, {message: 'Veuillez saisir la tva des frais'}).nullable(),
    notes: z.string().min(1, {message: 'Veuillez ajouter une note'}),
    customer_id: z.string().min(1, {message: 'Veuillez sélectionner un client.'}),
    billed_entity: z.object({
        id: z.number(),
        name: z.string(),
        type: z.enum(["customer", "contractor"]),
        is_synced: z.boolean().optional()
    }).nullable()
}).refine((data) => {
    const invoiceType = data.type?.name;
    
    // Si type "Honoraires et frais", les deux champs doivent être remplis (> 0)
    if (invoiceType === InvoiceType.HONORAIRES_ET_FRAIS) {
        return (data.billable !== null && data.billable > 0) && 
               (data.expenses !== null && data.expenses > 0);
    }
    
    // Si type "Honoraires", billable doit être > 0 et expenses doit être 0
    if (invoiceType === InvoiceType.HONORAIRES) {
        return (data.billable !== null && data.billable > 0) && 
               (data.expenses === null || data.expenses === 0);
    }
    
    // Si type "Frais", expenses doit être > 0 et billable doit être 0
    if (invoiceType === InvoiceType.FRAIS) {
        return (data.expenses !== null && data.expenses > 0) && 
               (data.billable === null || data.billable === 0);
    }
    
    return true;
}, {
    message: 'Les montants ne correspondent pas au type de facture sélectionné.',
    path: ['type']
});

// ↓ type of the form schema
type NewInvoiceFormSchemaType = z.infer<typeof NewInvoiceFormSchema>;

type InvoicePopupProps = {
    dealData: GetDealByIdSchemaType;
    setView: (view: boolean) => void;
    refreshData: () => void;
    blueStyle: boolean;
    cancelDisabled: boolean;
    dealRecipients: { options: any[]; hasNextPage: boolean };
    handleGetDealRecipients: (value: string, page?: number) => Promise<void>;
}

const InvoicePopup = ({
    dealData,
    setView,
    refreshData,
    blueStyle,
    cancelDisabled,
    dealRecipients,
    handleGetDealRecipients
}: InvoicePopupProps) => {
    // -- states --
    const [disabled, setDisabled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<{ title: string; message: string; }[] | null>(null);
    const [banks, setBanks] = useState<{ options: Bank[], hasNextPage: boolean } | null>(null);

    const vat = useSelector((state: RootState) => state.GlobalsReducer.vat);

    const {handleSubmit, control, formState: {isValid}, setValue, trigger} = useForm<NewInvoiceFormSchemaType>(
        {
            defaultValues: {
                status: dealData.status && (typeof dealData.status === 'number') && (dealData.status <= 300) ? {name: InvoiceStatus.PREVISIONNELLE} : null,
                billing_date: dayjs().toDate(),
                billable: null,
                expenses: null,
                bank: null,
                type: null,
                due_date: dayjs().add(1, 'month').toDate(),
                billable_vat_rate: vat,
                expenses_vat_rate: vat,
                notes: '',
                customer_id: dealData.customer?.id?.toString() ?? '',
                billed_entity: (dealData as any).billed_entity_id ? {
                    id: (dealData as any).billed_entity_id,
                    name: (dealData as any).billed_entity_name,
                    type: (dealData as any).billed_entity_type,
                    is_synced: true
                }: null
            }
        }
    );

    // Surveiller le type de facture pour adapter les validations
    const invoiceType = useWatch({ control, name: 'type' });
    const previousTypeRef = useRef<string | null>(null);

    const navigate = useNavigate();

    const isAppearingInDealDetails = location.pathname.includes(`deals/${dealData.id}`);

    // -- dropdowns values --

    // ↓ status dropdown values
    const statusDropdownValues = Object.values(InvoiceStatus).map((status) => ({name: status}));
    // ↓ types dropdown values
    const typesDropdownValues = Object.values(InvoiceType).map((type) => ({name: type}));

    const userData = useSelector((state: RootState) => state.UserReducer.userProfileData);

    // -- fetch data for select input --
    const handleGetBank = async (search: string, page = 1) => {
        await fetchSelectData({action: BankActions.getAllBanks, search:`&search=${search}`, page: page, setter: setBanks, userData, resultPath: 'data'});
    };

    useEffect(() => {
        // ↓ banks dropdown values
        (async () => {
            await handleGetBank('', 1);
        })();
    }, []);

    // Réinitialiser les valeurs selon le type de facture
    useEffect(() => {
        const currentType = invoiceType?.name;
        
        // Éviter de réexécuter si le type n'a pas changé
        if (!currentType || currentType === previousTypeRef.current) {
            return;
        }

        previousTypeRef.current = currentType;

        if (currentType === InvoiceType.HONORAIRES) {
            // Type "Honoraires" : mettre expenses à 0
            setValue('expenses', 0, { shouldValidate: false });
            trigger('expenses');
            trigger('billable');
        }
        else if (currentType === InvoiceType.FRAIS) {
            // Type "Frais" : mettre billable à 0
            setValue('billable', 0, { shouldValidate: false });
            trigger('billable');
            trigger('expenses');
        }
        else if (currentType === InvoiceType.HONORAIRES_ET_FRAIS) {
            // Type "Honoraires et frais" : réinitialiser si nécessaire
            trigger('billable');
            trigger('expenses');
        }
    }, [invoiceType?.name]);

    // -- handler functions --

    const onSubmit = async (data: NewInvoiceFormSchemaType) => {
        setErrors(null);
        setDisabled(true);
        setLoading(true);

        // Normaliser les valeurs selon le type de facture
        const invoiceType = data.type?.name;
        let billableValue: number | null = data.billable !== null ? Number(data.billable) : null;
        let expensesValue: number | null = data.expenses !== null ? Number(data.expenses) : null;

        if (invoiceType === InvoiceType.HONORAIRES) {
            expensesValue = 0;
        } else if (invoiceType === InvoiceType.FRAIS) {
            billableValue = 0;
        }

        // ↓ body to send to the post request (see API documentation)
        const invoice = {
            status: getStatusId(data.status?.name as InvoiceStatus),
            deal_id: dealData.id,
            billing_date: standardFormat(data.billing_date),
            billable: billableValue,
            expenses: expensesValue,
            bank_name: data.bank?.name,
            type: getTypeId(data.type?.name as InvoiceType),
            due_date: standardFormat(data.due_date),
            billable_vat_rate: (data.billable_vat_rate as number / 100).toString(),
            expenses_vat_rate: (data.expenses_vat_rate as number / 100).toString(),
            customer_id: dealData.customer?.id,  // Customer du deal (relation commerciale)
            billed_entity_type: data.billed_entity?.type,  // Destinataire facture PennyLane
            billed_entity_id: data.billed_entity?.id,      // Destinataire facture PennyLane
            notes: data.notes,
            official_id: generateRandomNumber()
        }

        // ↓ post - new invoice
        const result = await InvoiceActions.createInvoice(userData, invoice);

        if (!result.success) {
            setErrors(getErrors([result]));
        }
        else {
            await refreshData();
            notify('Votre facture à bien été créé');
            setView(false);


            if (isAppearingInDealDetails) {
                navigate(`/deals/${dealData.id}/invoices`);

            }
        }

        setLoading(false);
        setDisabled(false);
    };

    return (
        <ContainerCard
            title='Nouvelle Facture'
            coloredButtonDisplayed={false}
            overflow='overflow-visible'
        >
            <div className="flex flex-col px-4 pt-2 pb-6 lg:space-y-0 lg:flex-row lg:space-x-6">
                <div className='flex flex-col w-full lg:w-1/2'>
                    <div className="mb-2">
                        <Controller
                            name="status"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner un statut.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <SelectInput
                                    label='Statut'
                                    placeholder='Statut'
                                    inputRef={ref}
                                    value={value}
                                    error={error}
                                    options={statusDropdownValues}
                                    onChange={onChange}
                                    loading={!statusDropdownValues}
                                    labelKeys={['name']}
                                    disabled={dealData.status && (typeof dealData.status === 'number') && (dealData.status <= 300) ? true : false}
                                    required={true}
                                    blueStyle={blueStyle}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="billing_date"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner une date.'
                            }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <DateInput
                                    label={'Date de facturation'}
                                    onChange={onChange}
                                    value={frenchFormat(value) as string}
                                    error={error}
                                    required={true}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="billable"
                            control={control}
                            rules={{
                                validate: (value) => {
                                    const type = invoiceType?.name;
                                    if (!type) return true;
                                    
                                    if (type === InvoiceType.FRAIS) {
                                        // Pour "Frais", billable doit être 0
                                        return (value === 0 || value === null) || '';
                                    }
                                    
                                    if (type === InvoiceType.HONORAIRES || type === InvoiceType.HONORAIRES_ET_FRAIS) {
                                        // Pour "Honoraires" ou "Honoraires et frais", billable doit être > 0
                                        return (value !== null && value > 0) || '';
                                    }
                                    
                                    return true;
                                }
                            }}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Honoraires Montant HT [€]'}
                                    onChange={(e) => {
                                        const numValue = e === '' || e === null ? null : Number(e);
                                        onChange(numValue);
                                    }}
                                    value={value?.toString() ?? ''}
                                    error={error}
                                    required={invoiceType?.name !== InvoiceType.FRAIS}
                                    disabled={disabled || invoiceType?.name === InvoiceType.FRAIS}
                                    placeholder={invoiceType?.name === InvoiceType.FRAIS ? "0 (non applicable)" : "Honoraires Montant HT"}
                                    type="number"
                                    color={blueStyle ? 'blue' : 'orange'}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="expenses"
                            control={control}
                            rules={{
                                validate: (value) => {
                                    const type = invoiceType?.name;
                                    if (!type) return true;
                                    
                                    if (type === InvoiceType.HONORAIRES) {
                                        // Pour "Honoraires", expenses doit être 0
                                        return (value === 0 || value === null) || '';
                                    }
                                    
                                    if (type === InvoiceType.FRAIS || type === InvoiceType.HONORAIRES_ET_FRAIS) {
                                        // Pour "Frais" ou "Honoraires et frais", expenses doit être > 0
                                        return (value !== null && value > 0) || '';
                                    }
                                    
                                    return true;
                                }
                            }}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Frais Montant HT [€]'}
                                    onChange={(e) => {
                                        const numValue = e === '' || e === null ? null : Number(e);
                                        onChange(numValue);
                                    }}
                                    value={value?.toString() ?? ''}
                                    error={error}
                                    required={invoiceType?.name !== InvoiceType.HONORAIRES}
                                    disabled={disabled || invoiceType?.name === InvoiceType.HONORAIRES}
                                    placeholder={invoiceType?.name === InvoiceType.HONORAIRES ? "0 (non applicable)" : "Frais Montant HT"}
                                    type="number"
                                    color={blueStyle ? 'blue' : 'orange'}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="bank"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner une banque.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <SelectInput
                                    label='Compte bancaire'
                                    placeholder='Compte bancaire'
                                    inputRef={ref}
                                    value={value}
                                    error={error}
                                    options={banks?.options ?? []}
                                    hasNextPage={banks?.hasNextPage ?? false}
                                    fetchFunction={handleGetBank}
                                    loading={!banks}
                                    isSearchable={true}
                                    onChange={onChange}
                                    labelKeys={['name']}
                                    required={true}
                                    disabled={disabled}
                                    blueStyle={blueStyle}
                                />
                            )}
                        />
                    </div>
                </div>
                <div className='flex flex-col w-full lg:w-1/2'>
                    <div className="mb-2">
                        <Controller
                            name="type"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner un type de facture.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <SelectInput
                                    label='Type'
                                    placeholder='Type'
                                    inputRef={ref}
                                    value={value}
                                    error={error}
                                    options={typesDropdownValues}
                                    loading={!typesDropdownValues}
                                    disabled={disabled}
                                    onChange={onChange}
                                    labelKeys={['name']}
                                    required={true}
                                    blueStyle={blueStyle}
                                />
                            )}
                        />
                    </div>

                    <div className="mb-2">
                        <Controller
                            name="billed_entity"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner un destinataire.'
                            }}}
                            render={({
                                field: {onChange, value, ref},
                                fieldState: {error}
                            }) => (
                                <>
                                    <SelectInput
                                        label='Destinataire'
                                        placeholder='Destinataire'
                                        inputRef={ref}
                                        value={value}
                                        error={error}
                                        hasNextPage={dealRecipients?.hasNextPage}
                                        options={dealRecipients?.options}
                                        loading={!dealRecipients}
                                        fetchFunction={handleGetDealRecipients}
                                        isSearchable={true}
                                        disabled={disabled}
                                        onChange={onChange}
                                        labelKeys={['name']}
                                        required={true}
                                        blueStyle={blueStyle}
                                    />
                                    {value && !value.is_synced && (
                                        <div className="flex items-start mt-1 text-xs text-orange-500">
                                            <span className="mr-1">⚠️</span>
                                            <span>
                                                Cette destinataire n&apos;est pas synchronisée avec PennyLane.
                                                La facture ne pourra être émise qu&apos;après synchronisation.
                                            </span>
                                        </div>
                                    )}
                                </>
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="due_date"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez sélectionner une date.'
                            }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <DateInput
                                    label={"Date d'écheance"}
                                    onChange={onChange}
                                    value={frenchFormat(value) as string}
                                    error={error}
                                    required={true}
                                    disabled={disabled}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="billable_vat_rate"
                            control={control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Veuillez saisir la tva d\'honoraires'
                                }
                            }}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Honoraires TVA [%]'}
                                    onChange={onChange}
                                    value={value?.toString() ?? ''}
                                    error={error}
                                    required={true}
                                    disabled={disabled}
                                    placeholder="Honoraires TVA"
                                    type="number"
                                    color={blueStyle ? 'blue' : 'orange'}
                                />
                            )}
                        />
                    </div>
                    <div className="mb-2">
                        <Controller
                            name="expenses_vat_rate"
                            control={control}
                            rules={{required: {
                                value: true,
                                message: 'Veuillez saisir la tva des frais'
                            }}}
                            render={({
                                field: {onChange, value},
                                fieldState: {error}
                            }) => (
                                <TextInput
                                    label={'Frais TVA [%]'}
                                    onChange={onChange}
                                    value={value?.toString() ?? ''}
                                    error={error}
                                    required={true}
                                    disabled={disabled}
                                    placeholder="Frais TVA"
                                    type="number"
                                    color={blueStyle ? 'blue' : 'orange'}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            <div className="px-5">
                <div className="py-4 border-t border-borderGrey">
                    <Controller
                        name="notes"
                        control={control}
                        render={({
                            field: {onChange, value}
                        }) => (
                            <>
                                <div className="font-normal text-sm text-[#646464] mb-2">Veuillez ajouter une note</div>
                                <TextAreaInput
                                    placeholder={'Tapez votre texte'}
                                    maxLength={100}
                                    onChange={onChange}
                                    value={value}
                                    disabled={disabled}
                                />
                            </>
                        )}
                    />
                </div>
            </div>

            <div className="flex flex-col justify-center pb-2 mt-2">
                <div className="flex justify-center pb-2">
                    <Button
                        onClick={() => setView(false)}
                        type={'secondary'}
                        content={'Annuler'}
                        disabled={isAppearingInDealDetails && cancelDisabled ? true : disabled}
                    />
                    <Button
                        type={blueStyle ? 'blue' : 'primary'}
                        content={'Ajouter la facture'}
                        onClick={handleSubmit(onSubmit)}
                        loading={loading}
                        disabled={!isValid}
                    />
                </div>
                <Error errors={errors}/>
            </div>
        </ContainerCard>
    )
}

export default InvoicePopup;
